import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowUpRight, Check, ChevronDown } from 'lucide-react';
import type { StoryboardInputRecord } from '@agentic-ivy/shared-types';
import { cn } from '@/lib/utils';
import { storyboardApi } from '@/services/storyboardApi';
import { useOutlineEditQueueStore } from '@/stores/outlineEditQueueStore';
import { appliedIn, changeWords, splitStoryboardInputs } from '@/components/video-workspace/storyboard/feedbackWords';
import { YourMessage } from '@/components/studio/plan/Thread';
import { homeTags } from '@/lib/studioB/takeover';

interface Feedback {
  want: () => void;
  inputs: StoryboardInputRecord[] | null;
  versions: { version: number; createdAt: string }[];
  open: (version: number) => void;
}

const FeedbackContext = createContext<Feedback | null>(null);

/** Direction B (beta only): the video's storyboard notes for the cards in Ivy's thread, fetched once a card needs them. */
export function StoryboardFeedbackProvider({ conversationId, onOpen, children }: { conversationId: string; onOpen: (version: number) => void; children: ReactNode }) {
  const [wanted, setWanted] = useState(false);
  const [inputs, setInputs] = useState<StoryboardInputRecord[] | null>(null);
  const [versions, setVersions] = useState<Feedback['versions']>([]);

  const load = useCallback(async () => {
    try {
      const [list, info] = await Promise.all([storyboardApi.listInputs(conversationId), storyboardApi.get(conversationId)]);
      setInputs(list);
      setVersions(info.versions);
    } catch (err) {
      console.error('[Storyboard] Failed to load your notes', err);
    }
  }, [conversationId]);

  useEffect(() => {
    if (!wanted) return;
    void load();
    const onEvent = (e: Event) => {
      if ((e as CustomEvent).detail?.conversationId === conversationId) void load();
    };
    window.addEventListener('storyboard-input', onEvent);
    window.addEventListener('storyboard-updated', onEvent);
    return () => {
      window.removeEventListener('storyboard-input', onEvent);
      window.removeEventListener('storyboard-updated', onEvent);
    };
  }, [wanted, load, conversationId]);

  const value = useMemo(() => ({ want: () => setWanted(true), inputs, versions, open: onOpen }), [inputs, versions, onOpen]);
  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
}

export type SendPhase = 'sending' | 'has-it';

const Spinner = () => <span aria-hidden className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />;

/**
 * F4: a storyboard send as one "you" event: what you changed in the page's words, its picture, where it came from, and
 * its state (sending, Ivy has it, applied in vN, replaced). The page's words come with the note, or from this session's
 * send while the note does not carry them yet.
 */
function FeedbackCard({ id, version, phase }: { id: string; version: number; phase: SendPhase }) {
  const feedback = useContext(FeedbackContext);
  const want = feedback?.want;
  useEffect(() => want?.(), [want]);
  const [open, setOpen] = useState(false);
  const [pictureOk, setPictureOk] = useState(true);
  const record = feedback?.inputs?.find((i) => i.id === id);
  const kept = useOutlineEditQueueStore.getState().displayByInput[id];
  const change = record
    ? changeWords({ ...record, summaries: record.summaries ?? kept?.summaries, images: record.images ?? kept?.images })
    : kept?.summaries
      ? changeWords({ touched: Object.keys(kept.summaries), fields: {}, lock: false, ...kept })
      : null;
  const image = change?.image && /^https:\/\//.test(change.image) && pictureOk ? change.image : null;
  const applied = record?.status === 'applied' && feedback ? appliedIn(record, feedback.versions) : null;
  const several = !!change && !change.what.startsWith('You approved') && change.rows.length > 1;

  return (
    <div data-feedback-card={id} className="plan-rise flex w-[272px] max-w-full flex-col gap-2 rounded-[14px] rounded-br-[4px] bg-b-raised px-3 py-2.5 shadow-[inset_0_0_0_1px_hsl(var(--b-line))]">
      {change && (
        <div className="flex min-w-0 items-center gap-2.5">
          {image && <img src={image} alt="" onError={() => setPictureOk(false)} className="h-9 w-16 flex-none rounded-md object-cover shadow-[0_0_0_1px_hsl(var(--b-line))]" />}
          <div className="min-w-0">
            <p data-change-what className="text-[12px] leading-4 text-b-t3">{change.what}</p>
            {several ? (
              <button type="button" data-change-value aria-expanded={open} onClick={() => setOpen(!open)} className="flex items-center gap-1 text-left text-[14px] font-medium leading-5 text-b-t1">
                {change.value}
                <ChevronDown aria-hidden className={cn('h-3.5 w-3.5 text-b-t3 transition-transform', open && 'rotate-180')} />
              </button>
            ) : (
              <p data-change-value className="text-[14px] font-medium leading-5 text-b-t1">{change.value}</p>
            )}
          </div>
        </div>
      )}
      {several && open && (
        <ul data-change-rows className="flex flex-col gap-1 text-[13px] leading-[18px] text-b-t1">
          {change.rows.map((r, i) => <li key={i}>{r.label ? `${r.label} → ${r.value}` : r.value}</li>)}
        </ul>
      )}
      <div className="flex items-center justify-between gap-2 text-[12px] leading-4 text-b-t3">
        {feedback ? (
          <button type="button" aria-label={`Open v${version} of the storyboard`} title="Open the page you sent this from" onClick={() => feedback.open(version)} className="inline-flex items-center gap-0.5 hover:text-b-t1">
            From the storyboard · v{version}
            <ArrowUpRight className="h-3 w-3" />
          </button>
        ) : (
          <span>From the storyboard · v{version}</span>
        )}
        {applied && feedback ? (
          <button type="button" data-feedback-status aria-label={`Open v${applied} of the storyboard`} onClick={() => feedback.open(applied)} className="inline-flex items-center gap-1 text-success hover:underline">
            <Check aria-hidden className="h-3 w-3" />Applied in v{applied}
          </button>
        ) : record?.status === 'applied' ? (
          <span data-feedback-status className="inline-flex items-center gap-1 text-success"><Check aria-hidden className="h-3 w-3" />Applied</span>
        ) : record?.status === 'superseded' ? (
          <span data-feedback-status>Replaced by a later note</span>
        ) : phase === 'sending' ? (
          <span className="inline-flex items-center gap-1.5"><Spinner /><span data-feedback-status>Sending…</span></span>
        ) : (
          <span data-feedback-status className="inline-flex items-center gap-1 text-success"><Check aria-hidden className="h-3 w-3" />Ivy has it</span>
        )}
      </div>
    </div>
  );
}

/** Direction B's user message text: a card per storyboard note it carries, then what the person typed in its bubble. */
export function UserTextB({ text, phase = 'has-it' }: { text: string; phase?: SendPhase }) {
  const { inputs, rest } = splitStoryboardInputs(text);
  const { words, chips } = homeTags(rest);
  return (
    <div className="flex w-full flex-col items-end gap-1.5">
      {inputs.map((i) => <FeedbackCard key={i.id} id={i.id} version={i.version} phase={phase} />)}
      {words && <YourMessage>{words}</YourMessage>}
      {chips.length > 0 && (
        <div className="flex flex-wrap justify-end gap-1.5">
          {chips.map((chip) => (
            <span key={chip} data-user-chip className="rounded-full border border-b-line bg-b-panel px-2.5 py-0.5 text-b-meta text-b-t2">
              {chip}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
